import { useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateContract } from "@factory/query/contract.query";
import { ContractFormRule, ContractFormSchema } from "@factory/form/form.rule";
import { contractFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { LabelValue } from "@shared/models/label-value.interface";
import { useEffect } from "react";
import { useQyCreateBilling } from "@factory/query/billing.query";
import { GetContractModel } from "@factory/model/query-model-contract";
import { GetBillingModel } from "@factory/model/query-model-billing";

export function useNewContract() {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { mapCreateContract, mapCreateBilling } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();
  const { getUserRole } = useUserIdentityContext();
  let [searchParams] = useSearchParams();

  const defaultPatientCode = searchParams.get("code");
  const defaultPatientName = searchParams.get("name");

  useEffect(() => {
    if (defaultPatientCode) {
      setValue("patient", defaultPatientCode);
    }
  }, [defaultPatientCode]);

  useEffect(() => {
    if (defaultPatientName) {
      setValue("patientName", defaultPatientName);
    }
  }, [defaultPatientName]);

  // QUERIES
  const { mutateAsync: addContract, isLoading: isCreating } =
    useQyCreateContract();

  const { mutateAsync: addBilling } = useQyCreateBilling();

  // LOCAL FUNCTIONS
  const formMethod = useForm<ContractFormSchema>({
    defaultValues: contractFormDefault,
    resolver: zodResolver(ContractFormRule),
  });
  const { handleSubmit, setValue, watch } = formMethod;
  const patient = watch("patientName");
  const doctor = watch("personName");
  const service = watch("serviceName");
  const session = watch("session");

  const handleValidate = async (form: ContractFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreateContract(updatedForm);
    const contractResponse = await addContract(formData);
    const contractResponseData = contractResponse.data as GetContractModel;

    const billingData = mapCreateBilling({
      contract: contractResponseData.code,
      session: form.session,
    });
    try {
      await addBilling(billingData);
      navigate(`../${contractResponseData.code}`);
      showSuccess(`New Contract is created`);
    } catch {
      showError("Something went wrong with your request");
    }
  };
  const handleValidateError = (err: FieldErrors<ContractFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    patient,
    doctor,
    service,
    session,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
